import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    GridItem,
    Heading,
    Input,
    Select,
    Textarea,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { MultiSelectBuild } from 'components/molecules'
import { optionsDefault } from 'components/molecules/SelectBuild/constants'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import jwt_decode from 'jwt-decode'
import { AuthTokens } from 'models'
import { BottonLine, FormContainer } from 'pages/Report/styles'
import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { generateDebitNote } from 'services/debitNote'
import { generateBlobFile } from 'utils/blob'
import { schema } from './schema'
import { DebitNoteRequestDTO, FormDebitNote } from './types'

const getMesAnterior = (): string => {
    const data = new Date()
    data.setMonth(data.getMonth() - 1)

    const ano = data.getFullYear()
    const mes = String(data.getMonth() + 1).padStart(2, '0')

    return `${ano}-${mes}`
}

const getDataAtual = (): string => {
    const data = new Date()
    const ano = data.getFullYear()
    const mes = String(data.getMonth() + 1).padStart(2, '0')
    const dia = String(data.getDate()).padStart(2, '0')

    return `${ano}-${mes}-${dia}`
}

export const DebitNote: React.FC = () => {
    const { currentUser } = useAuthContext()
    const { currentInstitution, currentAgent } = useInstitutionsContext()

    const [loading, setLoading] = useState(false)

    const toast = useToast()

    const decodedIdToken: AuthTokens = jwt_decode(
        currentUser?.idToken as string
    )

    const detranUsuario = useMemo(() => {
        const ufDetran = decodedIdToken?.UFDetran

        const detranEncontrado = optionsDefault.ufContract.some(
            option => option.value === ufDetran
        )

        return detranEncontrado ? ufDetran : ''
    }, [decodedIdToken?.UFDetran])

    const {
        register,
        control,
        handleSubmit,
        formState: { errors }
    } = useForm<FormDebitNote>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            tipo: 'SERVICO',
            periodo: getMesAnterior(),
            detran: detranUsuario,
            dataEmissao: getDataAtual(),
            vencimento: '',
            quantidade: 1,
            descricao: '',
            tipoGeracao: 'CONSOLIDADA'
        }
    })

    const exibirErro = (titulo: string, descricao: string) => {
        toast({
            title: titulo,
            description: descricao,
            position: 'bottom-left',
            status: 'error',
            isClosable: true
        })
    }

    const gerarNotaDebito = async (form: FormDebitNote) => {
        if (!currentInstitution?.id) {
            exibirErro(
                'Instituição Financeira obrigatória',
                'Selecione uma Instituição Financeira para continuar.'
            )
            return
        }

        if (!currentAgent?.cnpj) {
            exibirErro(
                'Agente Financeiro obrigatório',
                'Selecione um Agente Financeiro para gerar a Nota de Débito.'
            )
            return
        }

        const [ano, mes] = form.periodo.split('-')
        const ultimoDia = new Date(Number(ano), Number(mes), 0).getDate()

        const request: DebitNoteRequestDTO = {
            tipo: form.tipo,
            instituicaoFinanceira: currentInstitution.id,
            cnpjAgente: currentAgent.cnpj.replace(/\D/g, ''),
            periodoInicial: `${ano}-${mes}-01`,
            periodoFinal: `${ano}-${mes}-${String(ultimoDia).padStart(
                2,
                '0'
            )}`,
            detran: form.detran || undefined,
            dataEmissao: form.dataEmissao,
            vencimento: form.vencimento,
            quantidade: Number(form.quantidade),
            descricao: form.descricao.trim(),
            consolidar: form.tipoGeracao === 'CONSOLIDADA'
        }

        setLoading(true)

        try {
            const response = await generateDebitNote(request)

            generateBlobFile(response, 'nota-debito.pdf')

            toast({
                title: 'Nota de Débito gerada com sucesso!',
                description:
                    'O PDF foi gerado conforme os filtros selecionados.',
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            exibirErro(
                'Não foi possível gerar a Nota de Débito',
                'Verifique os filtros e os dados de emissão cadastrados para o agente.'
            )
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingBottom="24px"
            >
                <Heading size="md">Gerar Nota de Débito</Heading>

                <Button
                    colorScheme="primary"
                    isLoading={loading}
                    loadingText="Gerando PDF"
                    onClick={handleSubmit(gerarNotaDebito)}
                >
                    Gerar PDF
                </Button>
            </Box>

            <Box
                paddingTop="24px"
                paddingX={8}
                bgColor="white"
                borderRadius={6}
                height="100%"
            >
                <form onSubmit={handleSubmit(gerarNotaDebito)}>
                    <FormContainer
                        minH={{
                            base: 'calc(100vh - 310px)',
                            md: 'calc(100vh - 260px)'
                        }}
                    >
                        <Grid
                            templateColumns={{
                                base: 'repeat(1, 1fr)',
                                md: 'repeat(12, 1fr)'
                            }}
                            gap={6}
                        >
                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <FormControl
                                    isInvalid={!!errors.tipo}
                                    isRequired
                                >
                                    <FormLabel>Tipo da Nota de Débito</FormLabel>

                                    <Select {...register('tipo')}>
                                        <option value="SERVICO">Serviço</option>
                                        <option value="TAXA">Taxa</option>
                                    </Select>

                                    <FormErrorMessage>
                                        {errors.tipo?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <FormControl
                                    isInvalid={!!errors.periodo}
                                    isRequired
                                >
                                    <FormLabel>Período (Mês/Ano)</FormLabel>

                                    <Input
                                        type="month"
                                        {...register('periodo')}
                                    />

                                    <FormErrorMessage>
                                        {errors.periodo?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <Controller
                                    name="detran"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectBuild
                                            id="detran"
                                            label="DETRAN"
                                            optionsType="ufContract"
                                            placeholder="Todos"
                                            value={field.value || ''}
                                            isDisable={!!detranUsuario}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            err={errors.detran}
                                        />
                                    )}
                                />
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <FormControl
                                    isInvalid={!!errors.dataEmissao}
                                    isRequired
                                >
                                    <FormLabel>Data de Emissão</FormLabel>

                                    <Input
                                        type="date"
                                        {...register('dataEmissao')}
                                    />

                                    <FormErrorMessage>
                                        {errors.dataEmissao?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <FormControl
                                    isInvalid={!!errors.vencimento}
                                    isRequired
                                >
                                    <FormLabel>Vencimento</FormLabel>

                                    <Input
                                        type="date"
                                        {...register('vencimento')}
                                    />

                                    <FormErrorMessage>
                                        {errors.vencimento?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 3 }}>
                                <FormControl
                                    isInvalid={!!errors.quantidade}
                                    isRequired
                                >
                                    <FormLabel>Quantidade</FormLabel>

                                    <Input
                                        type="number"
                                        min={1}
                                        {...register('quantidade', {
                                            valueAsNumber: true
                                        })}
                                    />

                                    <FormErrorMessage>
                                        {errors.quantidade?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 5 }}>
                                <FormControl
                                    isInvalid={!!errors.tipoGeracao}
                                    isRequired
                                >
                                    <FormLabel>Tipo de geração</FormLabel>

                                    <Select {...register('tipoGeracao')}>
                                        <option value="CONSOLIDADA">
                                            Uma Nota de Débito consolidada
                                        </option>

                                        <option value="POR_LINHA">
                                            Uma Nota de Débito por lançamento
                                        </option>
                                    </Select>

                                    <FormErrorMessage>
                                        {errors.tipoGeracao?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={{ base: 12, md: 12 }}>
                                <FormControl
                                    isInvalid={!!errors.descricao}
                                    isRequired
                                >
                                    <FormLabel>Descrição</FormLabel>

                                    <Textarea
                                        minH="180px"
                                        resize="vertical"
                                        maxLength={500}
                                        placeholder="Informe a descrição que será apresentada na Nota de Débito"
                                        {...register('descricao')}
                                    />

                                    <FormErrorMessage>
                                        {errors.descricao?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={12}>
                                <BottonLine />
                            </GridItem>
                        </Grid>
                    </FormContainer>
                </form>
            </Box>
        </Box>
    )
}